import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import { StatusName } from "../factories/statuses/enums/status.enum";
import { WorkTypeModel } from "../factories/work-type/models/work-type.model";

export type UpdateWorkTypeStatusesPayload = {
  workTypeId: string;
  statuses: UpdateWorkTypeStatusModel[];
};

export type UpdateWorkTypeStatusModel = {
  workTypeStatusId: string;
  workTypeSubStatusIds: string[];
};

export type UpdateWorkTypeDefaultDataPayload = {
  workTypeId: string;
  defaultTemplateId: string | null;
  defaultAssigneeId: string | null;
  defaultRoleId: string | null;
  defaultBudget: number | null;
};

export const workTypeSlice = createApi({
  reducerPath: "workTypeSlice",
  tagTypes: ["WorkType"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getWorkTypes: build.query<
      WorkTypeModel[],
      {
        orgId: string;
        status?: "all" | "active" | "inactive";
      }
    >({
      query: ({ orgId, status = "active" }) => ({
        url: "api/services/get-all",
        params: { orgId, status },
        method: "GET",
      }),
      providesTags: ["WorkType"],
    }),
    searchWorkTypes: build.query<
      WorkTypeModel[],
      {
        orgId: string;
        query: string | null;
      }
    >({
      query: (params) => ({
        url: "api/services/search",
        params,
        method: "GET",
      }),
      providesTags: ["WorkType"],
    }),
    getDeactivatedWorkTypes: build.query<
      WorkTypeModel[],
      {
        orgId: string;
      }
    >({
      query: (params) => ({
        url: "api/services/get-hidden-services",
        params,
        method: "GET",
      }),
      providesTags: ["WorkType"],
    }),
    deleteWorkType: build.mutation<
      void,
      {
        id: string;
      }
    >({
      query: (body) => ({
        url: "api/services",
        body,
        method: "DELETE",
      }),
      invalidatesTags: ["WorkType"],
    }),
    deactivateWorkType: build.mutation<
      void,
      {
        id: string;
      }
    >({
      query: (body) => ({
        url: "api/services/hide-worktype",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["WorkType"],
    }),
    activateWorkType: build.mutation<
      void,
      {
        id: string;
      }
    >({
      query: (body) => ({
        url: "api/services/unhide-worktype",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["WorkType"],
    }),
    getWorkType: build.query<
      WorkTypeModel,
      {
        id: string;
      }
    >({
      query: ({ id }) => ({
        url: "api/services/" + id,
        method: "GET",
      }),
      transformResponse: (res: WorkTypeModel | null) => {
        // A deleted work type answers null (QA batch 10): keep it as it is.
        if (!res) return res as unknown as WorkTypeModel;
        return {
          ...res,
          defaultAutomation: res.defaultAutomation ?? [],
          serviceStatuses:
            res.serviceStatuses
              ?.filter((status) => status?.name !== StatusName.trial)
              .sort((a, b) => a.order - b.order) || [],
        };
      },
      providesTags: ["WorkType"],
    }),
    updateWorkTypeStatuses: build.mutation<
      unknown,
      UpdateWorkTypeStatusesPayload
    >({
      query: (body) => ({
        url: "api/services/add-service-statuses",
        body,
        method: "POST",
      }),
      invalidatesTags: ["WorkType"],
    }),
    updateWorkTypeDefaultData: build.mutation<
      unknown,
      UpdateWorkTypeDefaultDataPayload
    >({
      query: (body) => ({
        url: "api/services/default-data",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["WorkType"],
    }),
    updateWorkType: build.mutation<
      unknown,
      {
        id: string;
        name: string;
        description: string;
      }
    >({
      query: (body) => ({
        url: "api/services/update-service",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["WorkType"],
    }),
    /** Billable default for new time on this work type (null = no default). */
    updateWorkTypeBillableDefault: build.mutation<
      unknown,
      { id: string; billableDefault: boolean | null }
    >({
      query: (body) => ({
        url: "api/services/billable-default",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["WorkType"],
    }),
    /** Default due date of new work = start + N days (0..365, null = none). Roadmap 1849. */
    updateWorkTypeDefaultDueDays: build.mutation<
      unknown,
      { id: string; defaultDueDays: number | null }
    >({
      query: (body) => ({
        url: "api/services/default-due-days",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["WorkType"],
    }),
    addWorkType: build.mutation<
      { id: string },
      {
        name: string;
        description: string;
        orgId: string;
        isActive: boolean;
        locationRelayId: string | null;
      }
    >({
      query: (body) => ({
        url: "api/services/add-service",
        body,
        method: "POST",
      }),
      invalidatesTags: ["WorkType"],
    }),
  }),
});

export const {
  useUpdateWorkTypeBillableDefaultMutation,
  useUpdateWorkTypeDefaultDueDaysMutation,
  useGetWorkTypesQuery,
  useGetWorkTypeQuery,

  useLazyGetWorkTypeQuery,

  useLazyGetWorkTypesQuery,
  useLazyGetDeactivatedWorkTypesQuery,
  useDeleteWorkTypeMutation,
  useDeactivateWorkTypeMutation,
  useActivateWorkTypeMutation,
  useLazySearchWorkTypesQuery,

  useUpdateWorkTypeStatusesMutation,
  useUpdateWorkTypeDefaultDataMutation,
  useUpdateWorkTypeMutation,
  useAddWorkTypeMutation,
} = workTypeSlice;
